import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { recalledEnv, envFromPath, rememberEnv, type Env } from './lib/env';
import { AdminLayout } from './components/AdminLayout';
// Eager — small, needed for first paint and auth flow.
import { AuthCallback } from './pages/AuthCallback';
import { Login } from './pages/Login';
import { OAuthConsent, rememberOAuthResume, pendingOAuthResume } from './pages/OAuthConsent';

// Route-level code splitting (2026-05-16 — perf refactor). Each page is
// lazy-loaded so the initial JS bundle ships only the auth shell + the
// dashboard chunk. Switching routes fetches the destination chunk on
// demand (cached by the browser thereafter). Crucially, an editor on a
// light page (e.g. Analytics) no longer downloads EditorialQueue's 3000+
// lines of JS up front.
//
// The named-export → default-export wrapper at the lazy() call lets us
// avoid touching the page files. If a page is renamed to a default
// export later, the wrapper can be dropped to `() => import('./Foo')`.
const MyQueue              = lazy(() => import('./pages/MyQueue').then(m => ({ default: m.MyQueue })));
const SuggestedVotes       = lazy(() => import('./pages/SuggestedVotes').then(m => ({ default: m.SuggestedVotes })));
const VotesMaster          = lazy(() => import('./pages/VotesMaster').then(m => ({ default: m.VotesMaster })));
const Profiles             = lazy(() => import('./pages/Profiles').then(m => ({ default: m.Profiles })));
const Analytics            = lazy(() => import('./pages/Analytics').then(m => ({ default: m.Analytics })));
const Jurisdictions        = lazy(() => import('./pages/Jurisdictions').then(m => ({ default: m.Jurisdictions })));
const EditorialQueue       = lazy(() => import('./pages/EditorialQueue').then(m => ({ default: m.EditorialQueue })));
const SourceTrust          = lazy(() => import('./pages/SourceTrust').then(m => ({ default: m.SourceTrust })));
const Topics               = lazy(() => import('./pages/Topics').then(m => ({ default: m.Topics })));
const Dashboard            = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const TesterFeedback       = lazy(() => import('./pages/TesterFeedback').then(m => ({ default: m.TesterFeedback })));
const AdminErrors          = lazy(() => import('./pages/AdminErrors').then(m => ({ default: m.AdminErrors })));
const PromoterRuns         = lazy(() => import('./pages/PromoterRuns').then(m => ({ default: m.PromoterRuns })));
const TaskTracker          = lazy(() => import('./pages/TaskTracker').then(m => ({ default: m.TaskTracker })));
const PipelineTriage       = lazy(() => import('./pages/PipelineTriage').then(m => ({ default: m.PipelineTriage })));
const CurationReporting    = lazy(() => import('./pages/CurationReporting').then(m => ({ default: m.CurationReporting })));
const SystemHealth         = lazy(() => import('./pages/SystemHealth').then(m => ({ default: m.SystemHealth })));
const ScheduleDashboard    = lazy(() => import('./pages/ScheduleDashboard').then(m => ({ default: m.ScheduleDashboard })));
const Security             = lazy(() => import('./pages/Security').then(m => ({ default: m.Security })));
const QaTracker            = lazy(() => import('./pages/QaTracker').then(m => ({ default: m.QaTracker })));
const Personas             = lazy(() => import('./pages/Personas').then(m => ({ default: m.Personas })));

// Route fallback while a chunk loads. Subtle skeleton that matches the
// admin's restrained palette — surface-light bg, gray-text shimmer, sits
// inside the AdminLayout's main content area so the sidebar and header
// stay rendered.
function RouteSkeleton() {
  return (
    <div className="p-4 md:p-6 animate-pulse">
      <div className="h-7 w-48 bg-stone-200 rounded mb-2" />
      <div className="h-4 w-72 bg-stone-200 rounded mb-6" />
      <div className="space-y-2">
        {[0, 1, 2, 3, 4, 5].map(i => (
          <div key={i} className="h-16 bg-stone-200 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

/** /prototype/<rest>?<query> -> /stage/<rest>?<query>. The legacy aliases map on through the
 *  /stage redirects below (e.g. /prototype/editorial -> /stage/editorial -> /stage/sourced-votes). */
/** /<env>/topics?<query> -> /<env>/themes?<query> (Topics renamed Themes, 2026-10-27). */
function ThemesRedirect() {
  const { pathname, search } = useLocation();
  return <Navigate to={`${pathname.replace(/\/topics$/, '/themes')}${search}`} replace />;
}

function ProtoRedirect() {
  const { pathname, search } = useLocation();
  const rest = pathname.replace(/^\/prototype/, '') || '/dashboard';
  return <Navigate to={`/stage${rest}${search}`} replace />;
}

/**
 * One env's pages. Prod lives under /stage/* (the key predates the 2026-10-16 launch) and dev-stage under
 * /devstage/* (2026-10-27). Same pages, same order; each page reads its env from the path (useEnv), and
 * the session follows the path too (auth.tsx), so a dev-stage page can only ever act on dev-stage.
 * Pages pinned to Prod's client (PROD_ONLY) get no dev-stage route — on the dev-stage tab they would
 * show Prod data under a green strip; the catch-all sends such a link home instead.
 */
const PROD_ONLY = new Set(['backlog', 'account-ladder', 'golive', 'admin-errors', 'promoter-runs']);

function envRoutes(env: Env) {
  const b = `/${env}`;
  const all = [
    <Route key={`${b}-1`} path={`${b}`} element={<Navigate to={`${b}/dashboard`} replace />} />,
    <Route key={`${b}-2`} path={`${b}/dashboard`}      element={<Dashboard />} />,
    // Pipeline Status merged into System Health (2026-09-28); old links land there.
    <Route key={`${b}-3`} path={`${b}/pipeline-status`} element={<Navigate to={`${b}/system-health`} replace />} />,
    <Route key={`${b}-4`} path={`${b}/system-health`}   element={<SystemHealth />} />,
    <Route key={`${b}-5`} path={`${b}/curation-reporting`} element={<CurationReporting />} />,
    <Route key={`${b}-6`} path={`${b}/suggested-votes`} element={<SuggestedVotes />} />,
    // Renamed from Proposed Votes (founder, 2026-09-28); old links land on the new name.
    <Route key={`${b}-7`} path={`${b}/proposed-votes`} element={<Navigate to={`${b}/suggested-votes`} replace />} />,
    <Route key={`${b}-8`} path={`${b}/sourced-votes`}  element={<EditorialQueue />} />,
    <Route key={`${b}-9`} path={`${b}/upcoming-votes`} element={<VotesMaster bucket="upcoming" />} />,
    <Route key={`${b}-10`} path={`${b}/open-votes`}     element={<VotesMaster bucket="open" />} />,
    <Route key={`${b}-11`} path={`${b}/closed-votes`}   element={<VotesMaster bucket="closed" />} />,
    <Route key={`${b}-12`} path={`${b}/sample-votes`}   element={<VotesMaster bucket="sample" />} />,
    <Route key={`${b}-13`} path={`${b}/backlog`}        element={<TaskTracker />} />,
    <Route key={`${b}-14`} path={`${b}/schedule`}       element={<ScheduleDashboard />} />,
    <Route key={`${b}-15`} path={`${b}/pipeline-triage`} element={<PipelineTriage />} />,
    <Route key={`${b}-16`} path={`${b}/admin-errors`}   element={<AdminErrors />} />,
    <Route key={`${b}-17`} path={`${b}/promoter-runs`}  element={<PromoterRuns />} />,
    <Route key={`${b}-18`} path={`${b}/profiles`}       element={<Profiles />} />,
    <Route key={`${b}-19`} path={`${b}/analytics`}      element={<Analytics />} />,
    <Route key={`${b}-20`} path={`${b}/jurisdictions`}  element={<Jurisdictions />} />,
    <Route key={`${b}-21`} path={`${b}/source-trust`}   element={<SourceTrust />} />,
    <Route key={`${b}-22`} path={`${b}/themes`}         element={<Topics />} />,
    // Topics became Themes and moved next to Suggested Votes (founder 2026-10-27); old links keep their tab.
    <Route key={`${b}-22b`} path={`${b}/topics`}        element={<ThemesRedirect />} />,
    // Topic History + Topic Proposals merged into Topics as two tabs (2026-09-28).
    <Route key={`${b}-23`} path={`${b}/topic-history`}  element={<Navigate to={`${b}/themes?tab=map`} replace />} />,
    <Route key={`${b}-24`} path={`${b}/topic-proposals`} element={<Navigate to={`${b}/themes?tab=proposals`} replace />} />,
    <Route key={`${b}-25`} path={`${b}/tester-feedback`} element={<TesterFeedback />} />,
    <Route key={`${b}-26`} path={`${b}/security`}       element={<Security />} />,
    <Route key={`${b}-27`} path={`${b}/personas`}       element={<Personas />} />,
    <Route key={`${b}-28`} path={`${b}/golive`}         element={<QaTracker tracker="golive" />} />,
    <Route key={`${b}-29`} path={`${b}/account-ladder`} element={<QaTracker tracker="ladder" />} />,
    // Legacy URL redirects
    <Route key={`${b}-30`} path={`${b}/proposals`}      element={<Navigate to={`${b}/suggested-votes`} replace />} />,
    <Route key={`${b}-31`} path={`${b}/editorial`}      element={<Navigate to={`${b}/sourced-votes`} replace />} />,
    <Route key={`${b}-32`} path={`${b}/votes`}          element={<Navigate to={`${b}/open-votes`} replace />} />,
  ];
  if (env === 'stage') return all;
  return all.filter(el => !PROD_ONLY.has(String(el.props.path ?? '').slice(b.length + 1)));
}

/** Core app routes — always rendered when authenticated (or when Supabase is off).
 *  Lazy-loaded pages are wrapped in a single Suspense at the Routes level.
 *  AdminLayout is eager so the sidebar/top bar stay mounted across chunk
 *  fetches; only the Outlet content swaps to RouteSkeleton briefly. */
/** Where to land after sign-in: back to a pending AI-tool approval if one brought us here, otherwise home. */
function postLoginTarget(home: string): string {
  return pendingOAuthResume() ?? home;
}

/** Signed out: keep a pending AI-tool approval link so login can bring us back to it, then go to login. */
function RememberThenLogin() {
  const { pathname, search } = useLocation();
  if (pathname === '/oauth/consent' && search) {
    rememberOAuthResume(pathname + search);
  }
  // Carry the env this page belongs to onto /login. Without it, opening the dev-stage tab with no
  // dev-stage session would land on /login, resolve to the remembered env (Prod, signed in) and bounce
  // straight back to Prod — the tab could never be signed in to. Remembering it also makes "home"
  // after sign-in the dev-stage dashboard, not Prod's.
  const env = envFromPath(pathname);
  if (env) rememberEnv(env);
  return <Navigate to={env ? `/login?env=${env}` : '/login'} replace />;
}

function AuthenticatedRoutes() {
  // Env-less entry points land on the env the operator last chose, not on a
  // hardcoded Proto. Hardcoding would have quietly undone every Stage tab click
  // the moment anything redirected — login, the catch-all, a stale bookmark.
  const home = `/${recalledEnv()}/dashboard`;
  return (
    <Suspense fallback={<RouteSkeleton />}>
    <Routes>
      <Route path="/login" element={<Navigate to={postLoginTarget(home)} replace />} />
      <Route path="/" element={<Navigate to={home} replace />} />
      <Route element={<AdminLayout />}>
        {/* Personal */}
        <Route path="/my-queue" element={<MyQueue />} />
        {/* AI door approvals (security worklist B7.3): Supabase's OAuth server sends AI clients here. */}
        <Route path="/oauth/consent" element={<OAuthConsent />} />

        {/* Proto retired from the admin (CS-5, founder 2026-09-28): every /prototype/* URL —
            bookmarks, old links, the legacy /prototype/proposals|editorial|votes aliases —
            lands on the same page under /stage/*. The admin can no longer read or write Proto. */}
        <Route path="/prototype/*" element={<ProtoRedirect />} />

        {/* Prod (/stage/*) and dev-stage (/devstage/*) — the same pages against two projects. */}
        {envRoutes('stage')}
        {envRoutes('devstage')}
      </Route>
      <Route path="*" element={<Navigate to={home} replace />} />
    </Routes>
    </Suspense>
  );
}

/** Gate: if no authenticated admin session, show the Login page instead of
 *  the app. Cloudflare Zero Trust is still the outer gate (controls who can
 *  reach this app at all); Supabase Auth is the inner gate that maps the
 *  admin to an auth.uid() so RLS policies gated on admin_users actually fire.
 *
 *  Three-way route structure:
 *    /auth/callback  — always renders (completes the magic-link flow)
 *    /login          — renders Login when unauth'd, redirects to dashboard when auth'd
 *    everything else — AuthenticatedRoutes when auth'd, redirect to /login when not
 */
function AuthGate() {
  const { user, loading } = useAuth();
  const home = `/${recalledEnv()}/dashboard`;

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-light flex items-center justify-center">
        <div className="text-sm text-gray-text">Loading…</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/login"
        element={user ? <Navigate to={postLoginTarget(home)} replace /> : <Login />}
      />
      <Route
        path="/*"
        element={user ? <AuthenticatedRoutes /> : <RememberThenLogin />}
      />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}
